/* eslint-disable complexity */
import { requestAttachmentList } from 'store/api/application.api';

import { DetailsType, FormData, Attachment, AttachPayload } from 'types/application';

import {
    SECT_TYPE,
    SECT_FILER,
    SECT_APPLICANT,
    SECT_PE,
    SECT_EVENT_DETAILS,
    SECT_EVENT_VENUE,
    SECT_PERFORMER,
    CONTACT_APPLICANT,
    SECT_DOCS
} from 'common/application.const';
import { retrievePlatforms, attachmentPayloadSetting, attachmentRetrieve } from '../helpers';
import { formatDateTime } from 'utils/application.helpers';

export const formatRetrieve = async (content: any, caseTypeID: string, action?: string) => {
    const eventValues = content.Event || {};

    // Contact
    const filerValues = content.Filer || {};
    const filerContact = filerValues.Contact || {};
    const { Facebook, SISITICLive, Zoom, Youtube, Others } = eventValues;
    const formattedPlatform = retrievePlatforms({ Facebook, SISITICLive, Zoom, Youtube, Others });
    const applicantValues = content.Applicant || {};
    const applicantContact = applicantValues.Contact || {};
    const capacityValues = eventValues.Capacity || {};
    const eventAddress = eventValues.Address && eventValues.Address.length > 0 ? eventValues.Address[0]?.MailingAddress || {} : {};
    const applicantAddress =
        applicantValues.Address && applicantValues.Address.length > 0 ? applicantValues.Address[0]?.MailingAddress || {} : {};
    // Contact
    const eventPE = eventValues.PELicence || {};
    const dateTimeValues = (eventValues.DateAndTime || []).map((dateTime: { [key: string]: string }) => ({
        from: dateTime.StartDateTime,
        to: dateTime.EndDateTime
    }));
    const performerValues = (eventValues.Performer || []).map((performer: { [key: string]: string }) => ({
        artist: performer.Name,
        country: performer.Nationality,
        'group-band': performer.GroupBandName
    }));
    const valuesContact = {
        title: content.Title,
        [`${CONTACT_APPLICANT}-applicant-type`]: content.AppliedAs,
        [`${CONTACT_APPLICANT}-filer-mobile-no-country-code`]: filerContact.MobileNoCountryCode || '65',
        [`${CONTACT_APPLICANT}-filer-mobile-no-main`]: filerContact.MobileNo,
        [`${CONTACT_APPLICANT}-filer-email`]: filerContact.EmailAddress,
        [`${CONTACT_APPLICANT}-applicant-mobile-no-country-code`]: applicantContact.MobileNoCountryCode || '65',
        [`${CONTACT_APPLICANT}-applicant-mobile-no-main`]: applicantContact.MobileNo,
        [`${CONTACT_APPLICANT}-applicant-email`]: applicantContact.EmailAddress,
        [`${CONTACT_APPLICANT}-address-type`]: applicantAddress.AddressType || 'SG',
        [`${CONTACT_APPLICANT}-postal`]: applicantAddress.PostalCode,
        [`${CONTACT_APPLICANT}-block-house`]: applicantAddress.BlockNumber,
        [`${CONTACT_APPLICANT}-street`]: applicantAddress.StreetName,
        [`${CONTACT_APPLICANT}-floor`]: applicantAddress.FloorNumber,
        [`${CONTACT_APPLICANT}-unit`]: applicantAddress.UnitNumber,
        [`${CONTACT_APPLICANT}-building`]: applicantAddress.BuildingName,
        dateTime: dateTimeValues
    };
    const valuesPE = {
        [`${SECT_PE}-event-title`]: eventValues.Title,
        [`${SECT_PE}-event-date-time`]: dateTimeValues,
        [`${SECT_PE}-pe-no`]: eventPE.PELicenceNo,
        [`${SECT_PE}-pe-start`]: eventPE.PELicenceStartDate,
        [`${SECT_PE}-pe-end`]: eventPE.PELicenceEndDate
    };

    let eventForm = {};
    if (action === 'pe') {
        eventForm = {
            id: 'event-pe',
            formID: 'event-pe',
            hasError: true,
            values: valuesPE
        };
        return [eventForm];
    }

    if (action === 'contact-details') {
        eventForm = {
            id: 'contact-details',
            formID: 'contact-details',
            hasError: true,
            values: valuesContact
        };
        return [eventForm];
    }
    if (action === 'application-withdraw') {
        eventForm = {
            id: 'application-withdraw',
            formID: 'application-withdraw',
            hasError: true,
            values: {
                title: content.Title
            }
        };
        return [eventForm];
    }

    const valuesPerformer = {
        [`${SECT_PERFORMER}-event-title`]: eventValues.Title,
        [`${SECT_PERFORMER}-performer-list`]: performerValues
    };
    const docs0 = attachmentRetrieve(eventValues.SupportingDocuments);
    const docs1 = attachmentRetrieve(eventValues.SupportingDocumentsOthers);
    const docsPerformer = attachmentRetrieve(eventValues?.PerformerListDocs);

    const valuesEventTypes = {
        [`${SECT_EVENT_DETAILS}-event-title`]: eventValues.Title,
        [`${SECT_EVENT_DETAILS}-event-type`]: eventValues.Type,
        [`${SECT_EVENT_DETAILS}-event-sub-type`]: eventValues.SubType,
        [`${SECT_EVENT_DETAILS}-event-date-time`]: dateTimeValues,
        [`${SECT_EVENT_DETAILS}-remarks`]: eventValues.Remarks,
        [`${SECT_EVENT_DETAILS}-docs-list-0-link`]: eventValues.Link,
        [`${SECT_EVENT_DETAILS}-docs-list-0-docs`]: docs0,
        [`${SECT_EVENT_DETAILS}-docs-list-1-link`]: eventValues.LinkOthers,
        [`${SECT_EVENT_DETAILS}-docs-list-1-docs`]: docs1
    };

    const generalForm = {
        id: 'general',
        formID: 'general',
        hasError: true,
        title: 'Applicant/Filer Details',
        values: {
            [`${SECT_TYPE}-applicant-type`]: content.AppliedAs,
            [`${SECT_FILER}-id-type`]: filerValues.IDType,
            [`${SECT_FILER}-idno`]: filerValues.IDNo,
            [`${SECT_FILER}-salutaion`]: filerValues.Salutation,
            [`${SECT_FILER}-name`]: filerValues.Person?.pyFullName,
            [`${SECT_FILER}-mobile-no-country-code`]: filerContact.MobileNoCountryCode || '65',
            [`${SECT_FILER}-mobile-no-main`]: filerContact.MobileNo,
            [`${SECT_FILER}-office-no-country-code`]: filerContact.OfficeNoCountryCode || '65',
            [`${SECT_FILER}-office-no-main`]: filerContact.OfficeNo,
            [`${SECT_FILER}-email`]: filerContact.EmailAddress,
            [`${SECT_APPLICANT}-id-type`]: applicantValues.IDType,
            [`${SECT_APPLICANT}-idno`]: applicantValues.IDNo,
            [`${SECT_APPLICANT}-salutaion`]: applicantValues.Salutation,
            [`${SECT_APPLICANT}-name`]: applicantValues.Person?.pyFullName,
            [`${SECT_APPLICANT}-nationality`]: applicantValues.Nationality,
            [`${SECT_APPLICANT}-uen-no`]: applicantValues.CompanyUEN,
            [`${SECT_APPLICANT}-company`]: applicantValues.CompanyName,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: applicantContact.MobileNoCountryCode || '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: applicantContact.MobileNo,
            [`${SECT_APPLICANT}-office-no-country-code`]: applicantContact.OfficeNoCountryCode || '65',
            [`${SECT_APPLICANT}-office-no-main`]: applicantContact.OfficeNo,
            [`${SECT_APPLICANT}-email`]: applicantContact.EmailAddress,
            [`${SECT_APPLICANT}-address-type`]: applicantAddress.AddressType || 'SG',
            [`${SECT_APPLICANT}-postal`]: applicantAddress.PostalCode,
            [`${SECT_APPLICANT}-block-house`]: applicantAddress.BlockNumber,
            [`${SECT_APPLICANT}-street`]: applicantAddress.StreetName,
            [`${SECT_APPLICANT}-floor`]: applicantAddress.FloorNumber,
            [`${SECT_APPLICANT}-unit`]: applicantAddress.UnitNumber,
            [`${SECT_APPLICANT}-building`]: applicantAddress.BuildingName
        }
    };

    const summary = {
        id: 'summary',
        formID: 'summary',
        hasError: true,
        values: {
            [`summary-title`]: eventValues.Title,
            [`summary-application-name`]: 'Arts Entertainment Classification',
            [`summary-id`]: content.caseid,
            [`summary-status`]: content.CustomerStatus,
            [`summary-date-submission`]: formatDateTime(content.pxCreateDateTime, true)
        }
    };
    if (action === 'pe') {
        eventForm = {
            id: 'event-pe',
            formID: 'event-pe',
            hasError: true,
            values: valuesPE
        };
    } else if (action === 'performer') {
        eventForm = {
            id: 'event-performer',
            formID: 'event-performer',
            hasError: true,
            values: valuesPerformer
        };
    } else if (action === 'event-types') {
        eventForm = {
            id: 'event-types',
            formID: 'event-types',
            hasError: true,
            values: valuesEventTypes
        };
    } else if (action === 'download') {
        const uploadedFiles = (caseTypeID ? await requestAttachmentList(caseTypeID) : []) as AttachPayload[];
        let files = {};
        if (content?.CustomerStatus === 'Rejected' || content?.CustomerStatus === 'Withdrawn') {
            files = [
                {
                    id: 'aecReceipt',
                    data: ['AEC Receipt', 'PDF', 'Download'],
                    ...(uploadedFiles?.find((value: any) => value?.category === 'Receipt') ?? {})
                }
            ];
        } else {
            files = [
                {
                    id: 'aecCertificate',
                    data: ['AEC Certificate', 'PDF', 'Download'],
                    ...(uploadedFiles?.find((value: any) => value?.category === 'Certificate') ?? {})
                },
                {
                    id: 'aecReceipt',
                    data: ['AEC Receipt', 'PDF', 'Download'],
                    ...(uploadedFiles?.find((value: any) => value?.category === 'Receipt') ?? {})
                }
            ];
        }
        eventForm = {
            id: 'download',
            formID: 'download',
            hasError: true,
            values: { files }
        };
    } else if (action === 'contact-details') {
        eventForm = {
            id: 'contact-details',
            formID: 'contact-details',
            hasError: true,
            values: valuesContact
        };
    } else {
        eventForm = {
            id: 'event',
            formID: 'event',
            hasError: true,
            title: 'Event Details',
            values: {
                [`${SECT_PE}-pe-no`]: eventPE.PELicenceNo,
                [`${SECT_PE}-pe-type`]: eventPE.PELicenceType,
                [`${SECT_PE}-cat1`]: eventPE.Category1Public,
                [`${SECT_PE}-pe-start`]: eventPE.PELicenceStartDate,
                [`${SECT_PE}-pe-end`]: eventPE.PELicenceEndDate,
                [`${SECT_EVENT_DETAILS}-ref-no`]: eventValues.caseid,
                [`${SECT_EVENT_DETAILS}-event-title`]: eventValues.Title,
                [`${SECT_EVENT_DETAILS}-event-type`]: eventValues.Type,
                [`${SECT_EVENT_DETAILS}-event-sub-type`]: eventValues.SubType,
                [`${SECT_EVENT_DETAILS}-sub-type-desc`]: eventValues.SubTypeDescription,
                [`${SECT_EVENT_DETAILS}-language`]: eventValues.Language,
                [`${SECT_EVENT_DETAILS}-lang-desc`]: eventValues.LanguageDescription,
                [`${SECT_EVENT_DETAILS}-event-desc`]: eventValues.Description,
                [`${SECT_EVENT_DETAILS}-offered-online`]: eventValues.OfferedOnlineOrVOD,
                [`${SECT_PERFORMER}-provide-performer-list`]: eventValues.WishToProvidePerformersList,
                [`${SECT_EVENT_DETAILS}-online-platform`]: formattedPlatform,
                [`${SECT_EVENT_DETAILS}-ticketed-or-free`]: eventValues.OnlineComponent,
                [`${SECT_EVENT_DETAILS}-online-platform-others`]: eventValues.OtherPlatform,
                [`${SECT_EVENT_DETAILS}-in-out`]: capacityValues.IndoorOutdoor,
                [`${SECT_EVENT_DETAILS}-event-date-time`]: dateTimeValues,
                [`${SECT_EVENT_DETAILS}-admission-fee`]: capacityValues.AdmissionFee,
                [`${SECT_EVENT_DETAILS}-crowd-size`]: capacityValues.CrowdSize,
                [`${SECT_EVENT_VENUE}-address-type`]: eventAddress.AddressType || 'SG',
                [`${SECT_EVENT_VENUE}-postal`]: eventAddress.PostalCode,
                [`${SECT_EVENT_VENUE}-block-house`]: eventAddress.BlockNumber,
                [`${SECT_EVENT_VENUE}-street`]: eventAddress.StreetName,
                [`${SECT_EVENT_VENUE}-floor`]: eventAddress.FloorNumber,
                [`${SECT_EVENT_VENUE}-unit`]: eventAddress.UnitNumber,
                [`${SECT_EVENT_VENUE}-building`]: eventAddress.BuildingName,
                [`${SECT_EVENT_VENUE}-address1`]: eventAddress.AddressLine1,
                [`${SECT_EVENT_VENUE}-address2`]: eventAddress.AddressLine2,
                [`${SECT_EVENT_VENUE}-location-desc`]: eventAddress.LocationDescription,
                [`${SECT_PERFORMER}-performer-list`]: performerValues,
                [`${SECT_PERFORMER}-performerListDocs`]: docsPerformer,
                [`${SECT_DOCS}-docs-list-0-link`]: eventValues.Link,
                [`${SECT_DOCS}-docs-list-0-docs`]: docs0,
                [`${SECT_DOCS}-docs-list-1-link`]: eventValues.LinkOthers,
                [`${SECT_DOCS}-docs-list-1-docs`]: docs1,
                CustomerStatus: content.CustomerStatus ?? '',
                ApplicationName: content?.pyLabel ?? '',
                WithdrawalDate: formatDateTime(content?.WithdrawalDate, true ?? ''),
                WithdrawalRemarks: content.WithdrawalRemarks ?? '',
                RejectionDate: formatDateTime((content?.Officer ?? ([] as any[])).pop()?.AllocatedDate ?? '', true),
                RejectionRemarks: content.Officer?.length ? content.Officer[0]?.Remarks ?? '' : '',
                ConsumerAdvice: (content?.Decision ?? ([] as any[])).pop()?.ConsumerAdvice ?? '',
                RatingDescription: eventValues?.ClassificationRatingDescription ?? ''
            }
        };
        return [summary, generalForm, eventForm];
    }
    return [eventForm];
};

export const formatSave = async (details: DetailsType, caseTypeID: string, action?: string) => {
    const eventPE = details.forms.find((form: FormData) => form.id === 'event-pe')?.values || {};
    const eventWithdraw = details.forms.find((form: FormData) => form.id === 'application-withdraw')?.values || {};
    const eventPerformer = details.forms.find((form: FormData) => form.id === 'event-performer')?.values || {};
    const eventTypes = details.forms.find((form: FormData) => form.id === 'event-types')?.values || {};
    const contactDetails = details.forms.find((form: FormData) => form.id === 'contact-details')?.values || {};
    const performers = (eventPerformer[`${SECT_PERFORMER}-performer-list`] as { [key: string]: string }[]) || [];
    const dateAndTime = (eventPE[`${SECT_PE}-event-date-time`] as { [key: string]: string }[]) || [];
    let eventContent = {};

    if (action === 'pe') {
        eventContent = {
            PELicence: {
                pxObjClass: 'IMDA-IRIS-Data-PEL',
                PELicenceNo: eventPE[`${SECT_PE}-pe-no`],
                PELicenceType: eventPE[`${SECT_PE}-pe-type`],
                PELicenceStartDate: eventPE[`${SECT_PE}-pe-start`],
                PELicenceEndDate: eventPE[`${SECT_PE}-pe-end`],
                Category1Public: eventPE[`${SECT_PE}-cat1`]
            },
            Title: eventPE[`${SECT_PE}-event-title`],
            DateAndTime: dateAndTime.map((row: { [key: string]: string }) => ({
                pxObjClass: 'IMDA-IRIS-Data-DateAndTime',
                StartDateTime: row.from,
                EndDateTime: row.to
            }))
        };
    } else if (action === 'performer') {
        // const performersDocs = (eventPerformer[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || [];
        // const performersAction = await attachmentActions(performersDocs, `Event.PerformerListDocs`);

        // const filesToUploadPerformer = performersAction.toUpload;
        // const filesToAttachPerformer = await requestUpload(filesToUploadPerformer, 'PerformerDetails');

        // if (filesToAttachPerformer.length > 0) await requestAttach(filesToAttachPerformer, caseTypeID);

        // const uploadedFiles = caseTypeID ? await requestAttachmentList(caseTypeID) : [];
        eventContent = {
            Title: eventPerformer[`${SECT_PERFORMER}-event-title`],
            Performer: performers.map((row: { [key: string]: string }) => ({
                pxObjClass: 'IMDA-IRIS-Data-Performer',
                Name: row.artist,
                Nationality: row.country,
                GroupBandName: row['group-band']
            })),
            // WishToProvidePerformersList: 'Yes', // removed as BE requested
            PerformerListDocs: attachmentPayloadSetting((eventPerformer[`${SECT_PERFORMER}-performerListDocs`] || []) as Attachment[])
        };
    } else if (action === 'contact-details') {
        if (contactDetails[`${CONTACT_APPLICANT}-applicant-type`] === 'Filer') {
            return {
                content: {
                    Filer: {
                        Contact: {
                            EmailAddress: contactDetails[`${CONTACT_APPLICANT}-filer-email`],
                            MobileNoCountry: contactDetails[`${CONTACT_APPLICANT}-filer-mobile-no-country-code`],
                            MobileNo: contactDetails[`${CONTACT_APPLICANT}-filer-mobile-no-main`]
                        }
                    },
                    Applicant: {
                        Address: [
                            {
                                MailingAddress: {
                                    AddressType: contactDetails[`${CONTACT_APPLICANT}-address-type`],
                                    BlockNumber: contactDetails[`${CONTACT_APPLICANT}-block-house`],
                                    BuildingName: contactDetails[`${CONTACT_APPLICANT}-building`],
                                    FloorNumber: contactDetails[`${CONTACT_APPLICANT}-floor`],
                                    PostalCode: contactDetails[`${CONTACT_APPLICANT}-postal`],
                                    StreetName: contactDetails[`${CONTACT_APPLICANT}-street`],
                                    UnitNumber: contactDetails[`${CONTACT_APPLICANT}-unit`]
                                }
                            }
                        ],
                        Contact: {
                            EmailAddress: contactDetails[`${CONTACT_APPLICANT}-applicant-email`],
                            MobileNoCountry: contactDetails[`${CONTACT_APPLICANT}-applicant-mobile-no-country-code`],
                            MobileNo: contactDetails[`${CONTACT_APPLICANT}-applicant-mobile-no-main`]
                        }
                    }
                }
            };
        }
        return {
            content: {
                Applicant: {
                    Address: [
                        {
                            MailingAddress: {
                                AddressType: contactDetails[`${CONTACT_APPLICANT}-address-type`],
                                BlockNumber: contactDetails[`${CONTACT_APPLICANT}-block-house`],
                                BuildingName: contactDetails[`${CONTACT_APPLICANT}-building`],
                                FloorNumber: contactDetails[`${CONTACT_APPLICANT}-floor`],
                                PostalCode: contactDetails[`${CONTACT_APPLICANT}-postal`],
                                StreetName: contactDetails[`${CONTACT_APPLICANT}-street`],
                                UnitNumber: contactDetails[`${CONTACT_APPLICANT}-unit`]
                            }
                        }
                    ],
                    Contact: {
                        EmailAddress: contactDetails[`${CONTACT_APPLICANT}-applicant-email`],
                        MobileNoCountry: contactDetails[`${CONTACT_APPLICANT}-applicant-mobile-no-country-code`],
                        MobileNo: contactDetails[`${CONTACT_APPLICANT}-applicant-mobile-no-main`]
                    }
                }
            }
        };
    } else if (action === 'event-types') {
        // const attachments0 = (eventTypes[`${SECT_EVENT_DETAILS}-docs-list-0-docs`] as Attachment[]) || [];
        // const attachments1 = (eventTypes[`${SECT_EVENT_DETAILS}-docs-list-1-docs`] as Attachment[]) || [];
        // const attachments0Action = await attachmentActions(attachments0, `Event.SupportingDocuments`);
        // const attachments1Action = await attachmentActions(attachments1, `Event.SupportingDocumentsOthers`);
        const eventTypesDateAndTime = (eventTypes[`${SECT_EVENT_DETAILS}-event-date-time`] as { [key: string]: string }[]) || [];

        // const filesToUpload: Attachment[] = [...attachments0Action.toUpload, ...attachments1Action.toUpload];

        // const filesToAttach = await requestUpload(filesToUpload, 'SupportingDocuments');
        // if (filesToAttach.length > 0) await requestAttach(filesToAttach, caseTypeID);

        // const uploadedFiles = caseTypeID ? await requestAttachmentList(caseTypeID) : [];
        eventContent = {
            Title: eventTypes[`${SECT_EVENT_DETAILS}-event-title`],
            Remarks: eventTypes[`${SECT_EVENT_DETAILS}-remarks`] ?? '',
            Type: eventTypes[`${SECT_EVENT_DETAILS}-event-type`],
            SubType: eventTypes[`${SECT_EVENT_DETAILS}-event-sub-type`],
            Link: eventTypes[`${SECT_EVENT_DETAILS}-docs-list-0-link`] ?? '',
            SupportingDocuments: attachmentPayloadSetting((eventTypes[`${SECT_EVENT_DETAILS}-docs-list-0-docs`] || []) as Attachment[]),
            LinkOthers: eventTypes[`${SECT_EVENT_DETAILS}-docs-list-1-link`] ?? '',
            DateAndTime: eventTypesDateAndTime.map((row: { [key: string]: string }) => ({
                pxObjClass: 'IMDA-IRIS-Data-DateAndTime',
                StartDateTime: row.from,
                EndDateTime: row.to
            })),
            SupportingDocumentsOthers: attachmentPayloadSetting(
                (eventTypes[`${SECT_EVENT_DETAILS}-docs-list-1-docs`] || []) as Attachment[]
            )
        };
    } else if (action === 'application-withdraw') {
        return {
            content: {
                pyGotoStage: 'ALT1',
                WithdrawalRemarks: eventWithdraw[`aecWithdraw-reason`],
                WithdrawalDate: new Date()
            }
        };
    }

    return {
        content: {
            ...(action === 'pe' ? { PELicenceNo: eventPE[`${SECT_PE}-pe-no`] } : {}),
            // ...(action === 'performer' ? { WishToProvidePerformersList: 'Yes' } : {}), // removed as BE requested
            Event: {
                pxObjClass: 'IMDA-IRIS-Data-Event',
                IsContentChanges: !!(
                    (eventContent as any).SupportingDocuments?.length || (eventContent as any).SupportingDocumentsOthers?.length
                ),
                ...eventContent
            }
        }
    };
};

const eventPayload = {
    formatRetrieve,
    formatSave
};

export default eventPayload;
